// The pagination bar every console list shares: total (or "10 000+" when
// the server capped its count), page size, prev/next and jump-to-page.

import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PAGE_SIZES } from '../lib/usePagedList.js';

export function ListPagination({ list, className = '' }) {
  const { t } = useTranslation('console');
  const { page, pages, size, setPage, setSize, total, totalCapped, items } = list;
  const lastPage = !totalCapped && page >= pages;
  const totalLabel = totalCapped ? t('list.totalCapped') : t('list.total', { total });
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground ${className}`}>
      <div className="flex items-center gap-3">
        <span data-testid="list-total">{totalLabel}</span>
        <label className="flex items-center gap-1.5">
          <span className="text-xs">{t('list.perPage')}</span>
          <Select value={String(size)} onValueChange={(v) => setSize(Number(v))}>
            <SelectTrigger className="h-8 w-[76px]" aria-label={t('list.perPage')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label={t('list.prev')}>
          <ChevronLeft className="h-4 w-4" /> {t('list.prev')}
        </Button>
        <span className="flex items-center gap-1 text-xs">
          <input
            type="number"
            min={1}
            max={totalCapped ? undefined : pages}
            value={page}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n >= 1) setPage(totalCapped ? n : Math.min(n, pages));
            }}
            className="h-8 w-14 rounded border border-border px-2 text-center"
            aria-label={t('list.page')}
          />
          {!totalCapped && <span>/ {pages}</span>}
        </span>
        <Button variant="outline" size="sm" disabled={lastPage || (totalCapped && items.length < size)} onClick={() => setPage(page + 1)} aria-label={t('list.next')}>
          {t('list.next')} <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
